import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { loadTranslations, loadPageTranslationsSync } from './translationLoader';
import { interpolate } from './interpolation';
import { pluralizeWithCount } from './pluralization';
import { SUPPORTED_LANGUAGES, getLanguageByCode } from './languages';
import { getApiClient } from './apiClient';
import logger from './logger';

// Language icon component - clean globe icon
const LanguageIcon = ({ language, size = 16 }) => {
  const iconStyle = {
    width: size,
    height: size,
    display: 'inline-block',
    marginRight: '8px',
    flexShrink: 0,
    color: 'currentColor'
  };

  // Simple globe icon using SVG
  return (
    <svg 
      style={iconStyle}
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
      title={`${language.toUpperCase()} Language`}
    >
      <circle cx="12" cy="12" r="10"/>
      <line x1="2" y1="12" x2="22" y2="12"/>
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
    </svg>
  );
};

// Load common translations only
const commonTranslations = loadTranslations();

const I18nContext = createContext();

export const useTranslation = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useTranslation must be used within an I18nProvider');
  }
  return context;
};

const _ppDbg = () => typeof window !== 'undefined' && (process.env.NODE_ENV === 'development' || (window.location && window.location.search && window.location.search.includes('ppdebug')));

export const I18nProvider = ({ children }) => {
  if (_ppDbg()) {
    console.log('[PP-DBG] 10 I18nProvider render');
  }
  const navigate = useNavigate();
  const location = useLocation();
  
  // Helper to check if user is logged in (by checking if we can get a token)
  const isUserLoggedIn = async () => {
    try {
      const apiClient = getApiClient();
      // Try to get a token - if this fails or returns null, user is not logged in
      const token = await apiClient.getApiAccessToken();
      const isLoggedIn = !!token;
      logger.debug('I18N', 'isUserLoggedIn check', { isLoggedIn, hasToken: !!token });
      return isLoggedIn;
    } catch (error) {
      // Log as debug instead of error since MSAL not being initialized is expected during startup
      logger.debug('I18N', 'isUserLoggedIn check - MSAL not ready', { error: error.message });
      return false;
    }
  };

  // Get initial language from URL path, user document, localStorage, or browser
  const getInitialLanguage = async () => {
    const path = location.pathname;
    
    // Try to extract language from URL path
    const langFromPath = path.match(/^\/([a-z]{2})\//)?.[1];
    
    if (langFromPath) {
      const langInfo = getLanguageByCode(langFromPath);
      // Accept any supported language, not just those in commonTranslations
      // Page-specific translations will handle missing common translations
      if (langInfo) {
        return langFromPath;
      }
    }
    
    // For logged-in users, try to load from user document
    const loggedIn = await isUserLoggedIn();
    if (loggedIn) {
      try {
        const apiClient = getApiClient();
        const response = await apiClient.get('/v1/preferences/settings');
        const language = response.settings?.language ?? response.preferences?.language;
        if (response.success && language) {
          const langInfo = getLanguageByCode(language);
          if (langInfo) {
            return language;
          }
        }
      } catch (error) {
        logger.warn('I18N', 'Failed to load language from user document, falling back', { error: error.message });
      }
    }
    
    // Try localStorage (for non-logged-in users or fallback)
    const savedLang = localStorage.getItem('besper-language');
    if (savedLang) {
      const langInfo = getLanguageByCode(savedLang);
      // Accept any supported language
      if (langInfo) {
        return savedLang;
      }
    }
    
    // Try browser language
    const browserLang = navigator.language.split('-')[0];
    const browserLangInfo = getLanguageByCode(browserLang);
    // Accept any supported language, prefer if it's in commonTranslations for better experience
    if (browserLangInfo) {
      return browserLang;
    }
    
    return 'de'; // Default to German
  };

  // Get synchronous initial language (from URL or localStorage) for immediate render
  const getSyncInitialLanguage = () => {
    const path = location.pathname;
    const langFromPath = path.match(/^\/([a-z]{2})\//)?.[1];
    if (langFromPath) {
      const langInfo = getLanguageByCode(langFromPath);
      if (langInfo) {
        return langFromPath;
      }
    }
    const savedLang = localStorage.getItem('besper-language');
    if (savedLang) {
      const langInfo = getLanguageByCode(savedLang);
      if (langInfo) {
        return savedLang;
      }
    }
    return 'de';
  };

  const [language, setLanguage] = useState(getSyncInitialLanguage);
  const prevLanguageRef = React.useRef(language);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize language once on mount — do NOT include language in deps.
  // Including language would re-fire the async init on every language change, potentially
  // overriding the current language with the initial value and creating an update loop.
  useEffect(() => {
    if (_ppDbg()) {
      console.log('[PP-DBG] 11 I18nProvider useEffect(init) start', { isInitialized });
    }
    if (!isInitialized) {
      getInitialLanguage().then(lang => {
        // Use functional updater to compare against the CURRENT language (not the stale closure value)
        setLanguage(prev => {
          if (lang !== prev) {
            prevLanguageRef.current = lang;
            return lang;
          }
          return prev;
        });
        setIsInitialized(true);
      }).catch(err => {
        // Never let init failure block the app — just mark as initialized
        console.warn('[platform-public] I18nProvider init failed, using default', err && err.message);
        setIsInitialized(true);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally run only when isInitialized changes
  }, [isInitialized]);

  // Update URL when language changes (but not when location changes from navigation)
  useEffect(() => {
    if (_ppDbg()) {
      console.log('[PP-DBG] 12 I18nProvider useEffect(URL sync) start', {
        language,
        prevRef: prevLanguageRef.current,
        pathname: location.pathname
      });
    }
    // Only navigate if language actually changed (not just location)
    if (prevLanguageRef.current === language) {
      // Language didn't change, just update ref from location if needed
      // Match language code at start with optional trailing slash: /de or /de/
      const currentLangMatch = location.pathname.match(/^\/([a-z]{2})(\/|$)/);
      const urlLang = currentLangMatch?.[1];
      if (urlLang && urlLang !== language) {
        // URL has different language, update state to match URL
        const langInfo = getLanguageByCode(urlLang);
        if (langInfo) {
          setLanguage(urlLang);
          prevLanguageRef.current = urlLang;
          // Only update localStorage if user is not logged in
          isUserLoggedIn().then(loggedIn => {
            if (!loggedIn) {
              localStorage.setItem('besper-language', urlLang);
            }
          });
        }
      }
      return;
    }

    // Language changed, update URL
    prevLanguageRef.current = language;
    const currentPath = location.pathname;
    const currentSearch = location.search; // Preserve query parameters
    const currentHash = location.hash; // Preserve hash
    
    // Remove ALL consecutive language codes from the start of the path
    // Handles: /de, /de/, /de/es, /de/es/, etc.
    const pathWithoutLang = currentPath.replace(/^(\/[a-z]{2})+(\/|$)/, '');
    
    // Rebuild path with new language
    // If path was just language codes (e.g., /de or /de/es), result is empty string
    // If path had content after language (e.g., /de/some/path), keep the content
    let newPath;
    if (pathWithoutLang === '' || pathWithoutLang === '/') {
      // Path was just language code(s), set to new language root
      newPath = `/${language}/`;
    } else {
      // Path had content after language, prepend new language
      // Ensure pathWithoutLang starts with / (it should after the replace, but be safe)
      const cleanPath = pathWithoutLang.startsWith('/') ? pathWithoutLang : `/${pathWithoutLang}`;
      newPath = `/${language}${cleanPath}`;
    }
    
    const newUrl = newPath + currentSearch + currentHash;
    
    // Defer navigate to next tick to avoid sync recursion in React commit/passive effects
    // (RangeError: Maximum call stack size exceeded when navigate ran inside flushPassiveEffects)
    const doNavigate = () => {
      if (_ppDbg()) {
        console.log('[PP-DBG] 13 I18nProvider calling navigate', { newUrl, language });
      }
      navigate(newUrl, { replace: true });
      isUserLoggedIn().then(loggedIn => {
        if (!loggedIn) {
          localStorage.setItem('besper-language', language);
        }
      });
    };
    if (typeof requestAnimationFrame !== 'undefined') {
      requestAnimationFrame(doNavigate);
    } else {
      setTimeout(doNavigate, 0);
    }
  }, [language, navigate, location.pathname, location.search, location.hash]);

  const t = useCallback((key, pageNameOrDefault = null, options = {}) => {
    const keys = key.split('.');
    let value;
    
    // Determine if second argument is pageName or default value
    // Heuristic: if it's a string with spaces, quotes, or special chars, it's a default value
    // If it's a simple identifier-like string, it might be a pageName
    let pageName = null;
    let defaultValue = null;
    
    if (typeof pageNameOrDefault === 'string') {
      // Check if it looks like a default value (has spaces, quotes, or is a sentence)
      if (pageNameOrDefault.includes(' ') || pageNameOrDefault.includes("'") || pageNameOrDefault.includes('"') || pageNameOrDefault.length > 20) {
        defaultValue = pageNameOrDefault;
      } else {
        // Simple identifier - treat as pageName
        pageName = pageNameOrDefault;
      }
    } else if (pageNameOrDefault && typeof pageNameOrDefault === 'object' && !Array.isArray(pageNameOrDefault)) {
      // Object passed as second argument - it's options
      options = { ...pageNameOrDefault, ...options };
      // Check if defaultValue is in options
      if (options.defaultValue) {
        defaultValue = options.defaultValue;
      }
    }
    
    // Also check if defaultValue is in options (in case it was passed as third argument)
    if (options && options.defaultValue && !defaultValue) {
      defaultValue = options.defaultValue;
    }
    
    // Try page-specific translations first if pageName is provided
    if (pageName) {
      const pageTranslations = loadPageTranslationsSync(pageName);
      
      // Check if the requested language exists in translations
      if (!pageTranslations[language]) {
        // Language not found - this shouldn't happen if translation file is correct
        // But we'll fall through to fallback logic below
      } else {
        value = pageTranslations[language];
        
        // If the first key matches the pageName, skip it (e.g., 'marketplace.pagination.showing' with pageName='marketplace')
        // This handles the case where ui/marketplace.json has structure {de: {marketplace: {...}}}
        // OR where legacy files have structure {de: {marketplace: {...}}}
        if (value && keys.length > 0 && keys[0] === pageName) {
          // First, access the pageName key to get the actual page data
          value = value[pageName];
          // Then skip the first key and navigate the rest
          for (const k of keys.slice(1)) {
            value = value?.[k];
          }
        } else {
          // Normal lookup - keys might not include pageName
          for (const k of keys) {
            value = value?.[k];
          }
        }
      }
    }
    
    // Fallback to common translations
    if (!value) {
      // Try current language first
      if (commonTranslations && commonTranslations[language]) {
        value = commonTranslations[language];
        for (const k of keys) {
          if (value && typeof value === 'object' && k in value) {
            value = value[k];
          } else {
            value = undefined;
            break;
          }
        }
      }
      
      // If still not found, try English as fallback (even if current language is en, in case lookup failed)
      if (!value && commonTranslations && commonTranslations.en) {
        value = commonTranslations.en;
        for (const k of keys) {
          if (value && typeof value === 'object' && k in value) {
            value = value[k];
          } else {
            value = undefined;
            break;
          }
        }
      }
    }
    
    // Fallback to German if translation is missing
    if (!value && language !== 'de') {
      if (pageName) {
        const pageTranslations = loadPageTranslationsSync(pageName);
        if (pageTranslations.de) {
          value = pageTranslations.de;
          for (const k of keys) {
            value = value?.[k];
          }
        }
      }
      
      if (!value && commonTranslations.de) {
        value = commonTranslations.de;
        for (const k of keys) {
          value = value?.[k];
        }
      }
    }
    
    // Handle pluralization if count is provided
    if (options.count !== undefined && typeof value === 'object') {
      value = pluralizeWithCount(value, options.count, language);
    }
    
    // Handle interpolation if variables are provided
    if (typeof value === 'string' && (options.variables || (Object.keys(options).length > 0 && !options.defaultValue))) {
      const variables = options.variables || { ...options };
      // Remove count and defaultValue from variables if they were used
      if (options.count !== undefined) {
        variables.count = options.count;
      }
      delete variables.defaultValue;
      value = interpolate(value, variables);
    }
    
    // If still no value, try English as absolute last resort (even if current language is en)
    // This ensures we always try English even if the current language lookup failed
    if (!value && commonTranslations && commonTranslations.en) {
      value = commonTranslations.en;
      for (const k of keys) {
        if (value && typeof value === 'object' && k in value) {
          value = value[k];
        } else {
          value = undefined;
          break;
        }
      }
    }
    
    // CRITICAL: If value equals the key, that means translation wasn't found
    // In this case, use the default value if provided
    if ((!value || value === key) && defaultValue) {
      return defaultValue;
    }
    
    // If no value found and default value was provided, use it BEFORE returning key
    if (!value && defaultValue) {
      return defaultValue;
    }
    
    // Debug: Log if we're returning the key instead of a value (indicates translation not found)
    if ((!value || value === key) && key.includes('audienceSelectorModal.investorsRegulators')) {
      logger.warn('I18N', 'Translation not found, returning key', { 
        key, 
        language, 
        hasCommonTranslations: !!commonTranslations,
        hasLanguage: !!(commonTranslations && commonTranslations[language]),
        hasEn: !!(commonTranslations && commonTranslations.en),
        keys: keys,
        defaultValue: defaultValue || 'none',
        commonTranslationsKeys: commonTranslations ? Object.keys(commonTranslations) : [],
        value: value || 'undefined'
      });
      
      // If we have a default value but still no translation, something is wrong
      // Force return the default value to ensure user sees text, not keys
      if (defaultValue) {
        logger.error('I18N', 'CRITICAL: Translation lookup failed but default value exists - using default', {
          key,
          defaultValue
        });
        return defaultValue;
      }
    }
    
    // Return value if found, otherwise return key (shouldn't happen if default value was provided)
    // But if we have a default value, always use it instead of the key
    if (defaultValue && (!value || value === key)) {
      return defaultValue;
    }
    
    return value || key;
  }, [language]);

  const changeLanguage = useCallback(async (newLanguage) => {
    const langInfo = getLanguageByCode(newLanguage);
    if (langInfo || commonTranslations[newLanguage]) {
      const loggedIn = await isUserLoggedIn();
      logger.info('I18N', 'Changing language', { newLanguage, loggedIn });
      if (loggedIn) {
        try {
          const apiClient = getApiClient();
          logger.debug('I18N', 'Updating language in user document via API');
          const response = await apiClient.put('/v1/preferences/settings', {
            settings: { language: newLanguage }
          });
          logger.info('I18N', 'Language updated in user document', { response: response?.success });
        } catch (error) {
          logger.error('I18N', 'Failed to update language in user document', { error: error.message });
        }
      } else {
        logger.debug('I18N', 'User not logged in, storing language in localStorage');
        localStorage.setItem('besper-language', newLanguage);
      }
      setLanguage(newLanguage);
    }
  }, []);

  const loadContent = useCallback(async (pageName, contentType = 'md') => {
    try {
      const { loadPageContent } = await import('./contentLoader');
      return await loadPageContent(pageName, language, contentType);
    } catch (error) {
      logger.error('I18N', `Error loading content for ${pageName}`, { error: error.message });
      return null;
    }
  }, [language]);

  const availableLanguages = useMemo(() => SUPPORTED_LANGUAGES.map(lang => lang.code), []);
  const getLanguageInfo = useCallback((code) => getLanguageByCode(code), []);

  const value = useMemo(() => ({
    language,
    changeLanguage,
    t,
    loadContent,
    availableLanguages,
    supportedLanguages: SUPPORTED_LANGUAGES,
    getLanguageInfo
  }), [language, changeLanguage, t, loadContent, availableLanguages, getLanguageInfo]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
};

export const LanguageDropdown = ({ className = '', isOpen: controlledIsOpen, onToggle }) => {
  const { language, changeLanguage, availableLanguages, t } = useTranslation();
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const dropdownRef = React.useRef(null);
  
  // Use controlled state if provided, otherwise use internal state
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const setIsOpen = onToggle || setInternalIsOpen;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        if (isOpen) {
          setIsOpen(false);
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, setIsOpen]);

  return (
    <div ref={dropdownRef} className={`language-dropdown ${isOpen ? 'open' : ''} ${className}`}>
      <button 
        className="language-btn"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t('languageSelector.selectLanguage')}
        aria-expanded={isOpen}
      >
        <div className="language-btn-content">
          <LanguageIcon language={language} size={16} />
          <span className="lang-name-preview">
            {(() => {
              const langInfo = getLanguageByCode(language);
              // Always show native name only - never show abbreviations, codes, or flags
              // Fallback to language code if info not found (shouldn't happen, but prevents empty text)
              return langInfo 
                ? langInfo.nativeName 
                : (language || 'de').toUpperCase();
            })()}
          </span>
        </div>
        <span className={`dropdown-arrow ${isOpen ? 'open' : ''}`}></span>
      </button>
      
      <div className="language-options">
        {availableLanguages.map(langCode => {
          const langInfo = getLanguageByCode(langCode);
          // Always show native name only - never show abbreviations, codes, or flags
          // Fallback to language code if info not found (shouldn't happen, but prevents empty text)
          const displayName = langInfo 
            ? langInfo.nativeName 
            : langCode.toUpperCase();
          
          // Skip rendering if language info not found (shouldn't happen with SUPPORTED_LANGUAGES)
          if (!langInfo) return null;
          
          return (
            <button
              key={langCode}
              className={`language-option ${langCode === language ? 'active' : ''}`}
              onClick={() => {
                changeLanguage(langCode);
                setIsOpen(false);
              }}
              title={langInfo ? langInfo.nativeName : ''}
              aria-label={langInfo ? langInfo.nativeName : ''}
            >
              <span className="lang-name">
                {displayName}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};